import {
    ListCard,
    ListCardRow,
    Spinner,
    Body,
    Mono,
    StatusBadge,
    Badge,
    cn,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
    Text,
} from "@athena/ui";
import { useTranslation } from "react-i18next";
import type { ErrorLike } from "@apollo/client";
import type { TagsQuery } from "@/graphql/tags.generated";
import { Check, Plus } from "lucide-react";

interface CourseTagsTableProps {
    tags: TagsQuery["tags"] | null;
    /**
     * Tags the course carries through its lessons and videos rather than its own list — read-only
     * here, edited on the item they come from. Shown so the card's tags are never a mystery.
     */
    inheritedTags?: { id: string; name: string }[];
    tagsLoading: boolean;
    tagsError: ErrorLike | undefined;
    selectedTags: Set<string>;
    toggleTag: (itemId: string) => void;
    tagsModalOpen: boolean;
    setTagsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function CourseTagsTable({
    tags,
    inheritedTags = [],
    tagsLoading,
    tagsError,
    selectedTags,
    toggleTag,
    tagsModalOpen,
    setTagsModalOpen,
}: CourseTagsTableProps) {
    const { t } = useTranslation();

    if (!tags) return;

    return (
        <ListCard
            title={t("common.fields.tags")}
            description={t("admin.lms.tagDescription")}
            action={
                <Mono size="xs" muted>
                    {selectedTags.size} applied
                </Mono>
            }
        >
            <ListCardRow className="flex-col items-start gap-3">
                {tagsLoading ? (
                    <Spinner />
                ) : (
                    <div className="flex flex-wrap gap-1.5">
                        {tags
                            .filter((t) => selectedTags.has(t.id))
                            .map((tag) => (
                                <StatusBadge dot key={tag.id} variant={"tag"} onRemove={() => toggleTag(tag.id)}>
                                    {tag.name}
                                </StatusBadge>
                            ))}
                    </div>
                )}
                {tagsError && <Body>Unable to load tags at this time.</Body>}
                <DropdownMenu open={tagsModalOpen} onOpenChange={setTagsModalOpen}>
                    <DropdownMenuTrigger asChild>
                        <Badge
                            variant={"dottedOutline"}
                            className={cn(tagsModalOpen && "border-secondary border-dashed")}
                        >
                            <Plus className="mr-1 h-3.5 w-3.5" /> {t("admin.lms.addTag")}
                        </Badge>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-card w-48" align="start">
                        <DropdownMenuGroup>
                            {tags.map((tag) => (
                                <DropdownMenuItem
                                    key={tag.id}
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        toggleTag(tag.id);
                                    }}
                                    className="flex items-center justify-between"
                                >
                                    {tag.name}
                                    {selectedTags.has(tag.id) && <Check className="text-secondary h-3.5 w-3.5" />}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
                {selectedTags.size === 0 && inheritedTags.length === 0 && (
                    <Text size="sm" muted className="italic">
                        {t("admin.lms.tagsHelper")}
                    </Text>
                )}
                {inheritedTags.length > 0 && (
                    <div className="flex flex-col gap-1.5">
                        <Text size="xs" muted>
                            {t("admin.lms.inheritedTags")}
                        </Text>
                        <div className="flex flex-wrap gap-1.5">
                            {inheritedTags.map((tag) => (
                                <StatusBadge key={tag.id} variant={"muted"}>
                                    {tag.name}
                                </StatusBadge>
                            ))}
                        </div>
                    </div>
                )}
            </ListCardRow>
        </ListCard>
    );
}
